import React, { useRef, useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import {
  Col,
  Form,
  Input,
  Label,
  Offcanvas,
  OffcanvasBody,
  OffcanvasHeader,
} from 'reactstrap';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import { yupResolver } from '@hookform/resolvers/yup';
import { toast } from 'react-toastify';
import { initialValues, validationSchema } from './constants';
import { fetchHRCodeList } from '../../../store/apis/commonService';
import { createTicket } from '../../../store/apis/ticketService';
import { UserAsyncAutocompleteSelect, FileUploadDropzone } from '../../Common';
import { getAuthUser } from '../../../utils/auth.util';
import HRResponseibleIcon from '../../../assets/icons/hr-responsible.svg';

import './CreateTicket.scss';

function CreateTicket({ isOpen, toggle }) {
  const navigate = useNavigate();
  const filesRef = useRef(null);
  const queryClient = useQueryClient();
  const [ticketCategoryList, setTicketCategoryList] = useState([]);

  const {
    handleSubmit,
    control,
    reset,
    formState: { errors, isValid },
    watch,
    setValue,
    trigger,
    setError,
    clearErrors,
  } = useForm({
    defaultValues: initialValues,
    mode: 'onChange',
    resolver: yupResolver(validationSchema),
  });

  const watchValues = watch();

  const { data: ticketCategoryData } = useQuery({
    queryKey: ['ticketCategory'],
    queryFn: () =>
      fetchHRCodeList({
        type: 'HR_TICKET_CAT',
        param1: getAuthUser()?.hrCoCd.trim(),
      }),
    enabled: isOpen,
    keepPreviousData: true,
    staleTime: 0,
    cacheTime: 0,
    refetchOnMount: 'always',
  });

  useEffect(() => {
    if (ticketCategoryData) {
      const sortedCategories = [...ticketCategoryData]
        .sort((a, b) =>
          a?.Description?.trim().localeCompare(b?.Description?.trim()),
        )
        .map(category => ({
          value: category?.Description?.trim(),
          label: category?.Description?.trim(),
        }));
      setTicketCategoryList(sortedCategories);
    }
  }, [ticketCategoryData]);

  useEffect(() => {
    if (watchValues.category) {
      const selectedCategory = ticketCategoryData.find(
        category => category?.Description?.trim() === watchValues.category,
      );
      if (
        selectedCategory?.Responsible?.trim() ===
        getAuthUser()?.employeeId.trim()
      ) {
        setValue('hrResponsible', '', {
          shouldDirty: true,
        });
      } else {
        setValue(
          'hrResponsible',
          {
            value: selectedCategory?.Responsible?.trim(),
            label: selectedCategory?.Name?.trim(),
            name: selectedCategory?.Name?.trim(),
            code: selectedCategory?.Responsible?.trim(),
          },
          {
            shouldValidate: true,
            shouldDirty: true,
          },
        );
        trigger('hrResponsible');
      }
    } else {
      setValue('hrResponsible', '', {
        shouldDirty: true,
      });
    }
  }, [watchValues.category]);

  const { mutate: createTicketMutation, isPending: createTicketLoading } =
    useMutation({
      mutationFn: payload => createTicket(payload),
      onSuccess: data => {
        toast.success(`Ticket #${data?.ticketNo} created successfully!`, {
          className: 'toast-success',
        });
        queryClient.invalidateQueries(['ticketListing']);
        if (filesRef.current) {
          filesRef.current.value = '';
        }
        reset();
        // If url has /hr
        if (window.location.pathname.includes('/hr')) {
          navigate(`/hr/tickets/${data?.ticketNo}?fr=3`);
        } else {
          navigate(`/tickets/${data?.ticketNo}`);
        }
        toggle();
      },
      onError: error => {
        toast.error('Failed to create ticket. Please try again.', error, {
          className: 'toast-error',
        });
      },
    });

  const onSubmit = values => {
    // Check if assignedTo is the same as the logged-in user
    if (
      values.hrResponsible?.value.toLowerCase() ===
      getAuthUser()?.employeeId.toLowerCase()
    ) {
      toast.error('You cannot assign a ticket to yourself.', {
        className: 'toast-error',
      });
      return;
    }
    const formData = new FormData();
    formData.append('ticketType', values.category);
    formData.append('assignedTo', values.hrResponsible?.value);
    formData.append('ticketSubject', values.subject);
    formData.append('ticketDescription', values.message);
    formData.append('requestor', getAuthUser()?.employeeId || '');
    if (values.files && values.files.length > 0) {
      values.files.forEach(file => {
        formData.append('files', file);
      });
    }
    createTicketMutation(formData);
  };

  return (
    <Offcanvas
      isOpen={isOpen}
      toggle={toggle}
      direction="end"
      className="create-ticket-oc"
    >
      <OffcanvasHeader className="create-ticket-oc-header">
        Create New Ticket
      </OffcanvasHeader>
      <Form onSubmit={handleSubmit(onSubmit)}>
        <OffcanvasBody className="create-ticket-oc-body">
          <div className="d-flex flex-column gap-2 mt-2">
            <div>
              <Label className="my-1">
                Category<span className="text-danger">*</span>
              </Label>
              <Controller
                name="category"
                control={control}
                render={({ field }) => (
                  <Input
                    {...field}
                    type="select"
                    className="external-filter-input"
                    invalid={!!errors.category}
                    title="Select the category relevant for your ticket"
                  >
                    <option value="">Select Category</option>
                    {ticketCategoryList?.map(category => (
                      <option key={category?.value} value={category?.value}>
                        {category?.label}
                      </option>
                    ))}
                  </Input>
                )}
              />
              {errors.category && (
                <div className="invalid-feedback">
                  {errors.category.message}
                </div>
              )}
            </div>
            <div>
              <Label className="my-1">
                HR Responsible<span className="text-danger">*</span>
              </Label>
              <Controller
                name="hrResponsible"
                control={control}
                render={() => (
                  <UserAsyncAutocompleteSelect
                    autoCompleteFn={() =>
                      fetchHRCodeList({
                        type: 'TICKET_OWNER',
                        param1: getAuthUser()?.hrCoCd.trim(),
                      })
                    }
                    mappedFn={rawData => {
                      const unique = new Map();
                      rawData.forEach(item => {
                        const key = item?.Code?.trim();
                        const empId = getAuthUser()?.employeeId.trim();

                        if (key && key !== empId && !unique.has(key)) {
                          unique.set(key, {
                            value: key,
                            label: item?.Description?.trim(),
                            name: item?.Description?.trim(),
                            code: key,
                            image: item?.Imagefile,
                          });
                        }
                      });
                      return Array.from(unique.values());
                    }}
                    onChange={option => {
                      setValue('hrResponsible', option);
                      trigger('hrResponsible');
                    }}
                    placeholder="Search by Employee Name & ID"
                    tooltipTitle="This is the HR responsible that your ticket will be assigned to initially."
                    isClearable
                    value={watchValues.hrResponsible}
                    className={errors.hrResponsible ? 'is-invalid' : ''}
                    styles={{
                      control: (provided, state) => {
                        let { borderColor } = provided;
                        if (errors.hrResponsible) {
                          borderColor = 'red';
                        } else if (state.isFocused) {
                          borderColor = provided.borderColor;
                        }
                        return {
                          ...provided,
                          borderColor,
                          '&:hover': {
                            borderColor,
                          },
                          '&:focus': {
                            borderColor,
                          },
                          boxShadow: errors.hrResponsible
                            ? 'none'
                            : provided.boxShadow,
                        };
                      },
                    }}
                    defaultShowMenu
                    rightKeyAssessor="code"
                    isDisabled={watchValues.category === ''}
                    customRightIcon={
                      <span style={{ paddingRight: '10px' }}>
                        <img src={HRResponseibleIcon} alt="report to" />
                      </span>
                    }
                  />
                )}
              />
              {errors.hrResponsible && (
                <div className="invalid-feedback d-block">
                  {errors.hrResponsible.message}
                </div>
              )}
            </div>
            <div>
              <Label className="my-1">
                Subject<span className="text-danger">*</span>
              </Label>
              <Controller
                name="subject"
                control={control}
                render={({ field }) => (
                  <Input
                    {...field}
                    type="text"
                    placeholder="Enter Subject"
                    className="external-filter-input"
                    invalid={!!errors.subject}
                    title="Enter a brief and clear subject summarizing your request or concern."
                  />
                )}
              />
              {errors.subject && (
                <div className="invalid-feedback">{errors.subject.message}</div>
              )}
            </div>
            <div>
              <Label className="my-1">
                Message<span className="text-danger">*</span>
              </Label>
              <Controller
                name="message"
                control={control}
                render={({ field }) => (
                  <Input
                    {...field}
                    type="textarea"
                    rows="7"
                    placeholder="Enter Message"
                    className="external-filter-input"
                    invalid={!!errors.message}
                    title="Describe your issue or request in detail for HR to take proper action."
                  />
                )}
              />
              {errors.message && (
                <div className="invalid-feedback">{errors.message.message}</div>
              )}
            </div>
            <div>
              <Label className="my-1">Upload Attachment</Label>
              <Controller
                name="files"
                control={control}
                render={({ field }) => (
                  <FileUploadDropzone
                    error={errors.files}
                    showDownloadButton={false}
                    onDrop={async acceptedFiles => {
                      try {
                        await validationSchema.fields.files.validate(
                          acceptedFiles,
                        );
                        clearErrors('files');
                        setValue('files', acceptedFiles, {
                          shouldValidate: true,
                        });
                      } catch (err) {
                        setError('files', {
                          type: 'manual',
                          message: err.message,
                        });
                      }
                    }}
                    selectedFiles={field.value}
                    className={errors.files ? 'is-invalid' : ''}
                    handleDeleteFile={file => {
                      field.onChange(
                        field.value.filter(f => f.name !== file.name),
                      );
                    }}
                    multiple
                    tooltipTitle="Please attach relevant documents for your ticket"
                  />
                )}
              />
              {errors.files && (
                <div className="invalid-feedback">{errors.files.message}</div>
              )}
            </div>
          </div>
        </OffcanvasBody>
        <div className="create-ticket-oc-footer p-3 d-flex align-items-center gap-2">
          <Col>
            <button
              onClick={() => {
                toggle();
                reset();
              }}
              type="button"
              className="w-100 create-ticket-cancel"
            >
              Cancel
            </button>
          </Col>
          <Col>
            <button
              disabled={!isValid || createTicketLoading}
              type="submit"
              className="w-100 create-ticket-create"
            >
              {createTicketLoading ? 'Creating...' : 'Create'}
            </button>
          </Col>
        </div>
      </Form>
    </Offcanvas>
  );
}

export default CreateTicket;
